import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSecurity } from '../context/SecurityContext';

/**
 * Aviso flotante cuando el admin está logueado en la tienda.
 * Diseño limpio, esquina inferior derecha.
 */
const AdminBanner = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useSecurity();
  const [dismissed, setDismissed] = useState(false);

  if (!isAuthenticated || dismissed) return null;

  return (
    <div
      role="status"
      aria-label="Sesión de administrador"
      className="admin-banner-floating"
      style={{
        '--admin-banner-accent': '#00a1ff',
        '--admin-banner-bg': '#ffffff',
        '--admin-banner-text': '#1e293b',
        '--admin-banner-muted': '#334155',
      }}
    >
      <div className="admin-banner-inner">
        <div className="admin-banner-accent-bar" aria-hidden />
        <div className="admin-banner-content">
          <div className="admin-banner-compact">
            <div className="admin-banner-info">
              <span className="admin-banner-label">
                <i className="bi bi-shield-check" />
                <span className="admin-banner-text-full">Modo administrador</span>
                <span className="admin-banner-text-short">Admin</span>
              </span>
              <p className="admin-banner-message">
                <span className="admin-banner-message-full">Navegando como administrador</span>
                <span className="admin-banner-message-short">Admin activo</span>
              </p>
            </div>
            <div className="admin-banner-actions">
              <button
                type="button"
                onClick={() => navigate('/admin/dashboard')}
                className="admin-banner-cta"
              >
                <i className="bi bi-grid-1x2-fill" />
                <span className="admin-banner-cta-text">Panel</span>
              </button>
              <button
                type="button"
                onClick={() => setDismissed(true)}
                className="admin-banner-icon-btn admin-banner-icon-btn--close"
                title="Ocultar"
                aria-label="Ocultar aviso"
              >
                <i className="bi bi-x" />
              </button>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        .admin-banner-floating {
          position: fixed;
          bottom: 20px;
          right: 20px;
          left: auto;
          z-index: 1050;
          font-family: 'Open Sans', system-ui, 'Segoe UI', sans-serif;
          width: 320px;
          min-width: 280px;
        }
        @media (max-width: 768px) {
          .admin-banner-floating {
            width: 280px;
            min-width: 240px;
            bottom: 15px;
            right: 15px;
          }
        }
        @media (max-width: 480px) {
          .admin-banner-floating {
            width: 200px;
            min-width: 180px;
            bottom: 10px;
            right: 10px;
          }
        }
        .admin-banner-floating .admin-banner-inner {
          position: relative;
          background: var(--admin-banner-bg);
          border-radius: 16px;
          box-shadow: 0 4px 24px rgba(0, 161, 255, 0.1), 0 1px 3px rgba(0, 0, 0, 0.04);
          overflow: hidden;
          border: 1px solid rgba(0, 161, 255, 0.12);
          transition: box-shadow 0.25s ease;
        }
        .admin-banner-floating .admin-banner-inner:hover {
          box-shadow: 0 8px 32px rgba(0, 161, 255, 0.14), 0 2px 6px rgba(0, 0, 0, 0.05);
        }
        .admin-banner-accent-bar {
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          width: 4px;
          background: linear-gradient(180deg, var(--admin-banner-accent) 0%, #0096e8 100%);
          border-radius: 16px 0 0 16px;
        }
        .admin-banner-content {
          padding: 12px 16px 12px 20px;
        }
        .admin-banner-compact {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        }
        .admin-banner-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
          flex: 1;
          min-width: 0;
        }
        .admin-banner-label {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          font-size: 0.75rem;
          font-weight: 700;
          letter-spacing: 0.03em;
          text-transform: uppercase;
          color: var(--admin-banner-accent);
        }
        .admin-banner-label i {
          font-size: 0.9rem;
          opacity: 0.9;
        }
        .admin-banner-message {
          font-size: 0.8rem;
          color: var(--admin-banner-muted);
          line-height: 1.3;
          margin: 0;
          font-weight: 500;
        }
        .admin-banner-text-short,
        .admin-banner-message-short {
          display: none;
        }
        @media (max-width: 768px) {
          .admin-banner-text-full,
          .admin-banner-message-full {
            display: none;
          }
          .admin-banner-text-short,
          .admin-banner-message-short {
            display: inline;
          }
        }
        .admin-banner-actions {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-shrink: 0;
        }
        .admin-banner-cta {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 6px 12px;
          border: none;
          border-radius: 8px;
          font-size: 0.75rem;
          font-weight: 600;
          font-family: inherit;
          background: var(--admin-banner-accent);
          color: #fff;
          cursor: pointer;
          transition: background 0.2s, transform 0.15s;
          white-space: nowrap;
        }
        @media (max-width: 480px) {
          .admin-banner-cta {
            padding: 4px 8px;
            font-size: 0.7rem;
          }
          .admin-banner-cta-text {
            display: none;
          }
        }
        .admin-banner-cta:hover {
          background: #0096e8;
          transform: translateY(-1px);
        }
        .admin-banner-cta:active {
          transform: translateY(0);
        }
        .admin-banner-cta i {
          font-size: 0.9rem;
          opacity: 0.95;
        }
        .admin-banner-icon-btn {
          width: 28px;
          height: 28px;
          border: none;
          border-radius: 8px;
          background: transparent;
          color: var(--admin-banner-muted);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.9rem;
          transition: color 0.2s, background 0.2s;
        }
        @media (max-width: 480px) {
          .admin-banner-icon-btn {
            width: 24px;
            height: 24px;
            font-size: 0.8rem;
          }
        }
        .admin-banner-icon-btn:hover {
          background: rgba(0, 161, 255, 0.08);
          color: var(--admin-banner-accent);
        }
        .admin-banner-icon-btn--close:hover {
          background: rgba(220, 38, 38, 0.08);
          color: #dc2626;
        }
      `}</style>
    </div>
  );
};

export default AdminBanner;
